import { useEffect, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Undo2, Flag, ArrowLeft, Trophy, Lock, Unlock, XCircle, MapPin, Repeat, Video, ShieldCheck, CircleDot } from "lucide-react";
import MatchClock from "@/components/MatchClock";
import { shortTeam } from "@/lib/tournament";
import api, { formatApiError } from "@/lib/api";

function FaultDots({ count, total }) {
  return (
    <div className="flex items-center gap-1.5" data-testid="fault-dots">
      {Array.from({ length: total }).map((_, i) => (
        <span key={i} data-testid={`fault-dot-${i}`} className="w-3 h-3 rounded-full transition-colors"
          style={{ background: i < count ? "#ef4444" : "var(--border)", boxShadow: i < count ? "0 0 8px rgba(239,68,68,0.6)" : "none" }} />
      ))}
    </div>
  );
}

export function KitchenZone({ side, name, score, serving, position, isWinner, locked, faults, faultsTotal, band, onScore }) {
  const canScore = serving && !locked && !isWinner;
  return (
    <button
      data-testid={`kitchen-zone-${side}`}
      onClick={() => canScore && onScore()}
      disabled={!canScore}
      className="flex-1 rounded-3xl p-5 relative overflow-hidden text-center transition-transform active:scale-[0.98] disabled:cursor-default"
      style={{
        background: isWinner ? "rgba(34,197,94,0.16)" : serving ? "rgba(34,197,94,0.10)" : "var(--surface)",
        border: serving ? "2px solid #22c55e" : "1px solid var(--border)",
        boxShadow: serving && !locked ? "0 0 24px rgba(34,197,94,0.25)" : "none",
      }}>
      {serving && (
        <span className="absolute top-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 text-[10px] mono uppercase tracking-wider" style={{ color: "#22c55e" }} data-testid={`serving-${side}`}>
          serving · {position}
        </span>
      )}
      <div className="text-sm font-bold mt-5 truncate" data-testid={`zone-name-${side}`} title={name || ""}>{name ? shortTeam(name) : "—"}</div>
      {band && (
        <div className="flex items-center justify-center gap-1 mt-1 text-[10px] mono uppercase tracking-wider" style={{ color: "var(--primary-ink)" }} data-testid={`band-marker-${side}`}>
          <CircleDot className="w-3 h-3" /> {band}
        </div>
      )}
      <div className="font-display font-black tracking-tighter leading-none my-2" style={{ fontSize: "4.5rem" }} data-testid={`zone-score-${side}`}>{score}</div>
      {serving && faultsTotal > 1 && (
        <div className="flex items-center justify-center gap-2 mt-1">
          <span className="text-[10px] mono uppercase tracking-wider" style={{ color: "var(--text-2)" }}>faults</span>
          <FaultDots count={faults} total={faultsTotal} />
        </div>
      )}
      {canScore && <div className="text-[10px] mono uppercase tracking-wider mt-2" style={{ color: "#22c55e" }}>tap kitchen = point</div>}
    </button>
  );
}

export default function LiveScoring({ ref_, onBack, backLabel }) {
  const { tid, cid, mid } = ref_;
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pip, setPip] = useState(false);
  const [sideAlert, setSideAlert] = useState(false);
  const [swapped, setSwapped] = useState(false);
  const busyRef = useRef(false);
  const prevServing = useRef(null);
  const alertedAt = useRef(null);

  const load = useCallback(() => {
    api.get(`/umpire/scorecard?tid=${tid}&cid=${cid}&mid=${mid}`).then(({ data }) => setData(data)).catch(() => setData((d) => (d ? d : false)));
  }, [tid, cid, mid]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => { if (!busyRef.current) load(); }, 2000);
    return () => clearInterval(t);
  }, [load]);

  const sc = data && data !== false ? data.scorecard : null;

  // side-out flash + side-change alert
  useEffect(() => {
    if (!sc) return;
    if (prevServing.current && prevServing.current !== sc.serving_team && !sc.match_over) {
      const nm = sc.serving_team === "a" ? data.a_name : data.b_name;
      toast.info(`SIDE OUT — ${nm} now serving`, { duration: 2500 });
    }
    prevServing.current = sc.serving_team;
    const sideChangeAt = Math.ceil((sc.target + 1) / 2);
    const top = Math.max(sc.team_a_score, sc.team_b_score);
    if (top === sideChangeAt && alertedAt.current !== sideChangeAt && !sc.match_over) {
      alertedAt.current = sideChangeAt;
      setSideAlert(true);
      setSwapped((s) => !s); // players switch ends → flip the digital court
    }
  }, [sc, data]);

  const setBusyBoth = (v) => { busyRef.current = v; setBusy(v); };

  const rally = async (action) => {
    if (pip) { toast.error("Point in progress is locked — toggle PIP to resume"); return; }
    setBusyBoth(true);
    try {
      const { data: res } = await api.post(`/umpire/scorecard/rally`, { tid, cid, mid, action });
      setData((d) => ({ ...d, scorecard: res }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Action failed"); }
    finally { setBusyBoth(false); }
  };

  const undo = async () => {
    setBusyBoth(true);
    try {
      const { data: res } = await api.post(`/umpire/scorecard/undo`, { tid, cid, mid });
      setData((d) => ({ ...d, scorecard: res }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Nothing to undo"); }
    finally { setBusyBoth(false); }
  };

  const finalize = async () => {
    setBusyBoth(true);
    try {
      const { data: res } = await api.post(`/umpire/scorecard/finalize`, { tid, cid, mid });
      toast.success("Match finalized & audit log synced");
      setData((d) => ({ ...d, scorecard: res.scorecard, match_winner: res.winner === "a" ? d.a_name : d.b_name }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Cannot finalize yet"); }
    finally { setBusyBoth(false); }
  };

  if (data === null) return <div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div>;
  if (data === false) return <p className="text-center text-sm py-24" style={{ color: "var(--text-2)" }}>Could not load scorecard.</p>;

  const servingName = sc.serving_team === "a" ? data.a_name : data.b_name;
  const winnerName = data.match_winner || (sc.winner === "a" ? data.a_name : sc.winner === "b" ? data.b_name : null);
  const done = sc.finalized || !!data.match_winner;
  const challenge = sc.setup?.challenge_court;

  return (
    <div data-testid="live-scoring">
      <button data-testid="scorecard-back" onClick={onBack} className="text-sm flex items-center gap-2 mb-4" style={{ color: "var(--text-2)" }}><ArrowLeft className="w-4 h-4" /> {backLabel}</button>

      <div className="flex items-center justify-center gap-2 mb-3 flex-wrap">
        {data.court_name && <span data-testid="scorecard-court" className="flex items-center gap-1.5 text-xs mono uppercase tracking-wider text-primary-c"><MapPin className="w-3.5 h-3.5" /> {data.court_name}</span>}
        {challenge && <span data-testid="challenge-badge" className="flex items-center gap-1.5 text-xs mono uppercase tracking-wider glass rounded-full px-2.5 py-1" style={{ color: "var(--primary-ink)" }}><Video className="w-3.5 h-3.5" /> Challenge court</span>}
      </div>

      {done ? (
        <div className="rounded-3xl glow-border p-10 text-center" style={{ background: "rgba(34,197,94,0.10)" }} data-testid="scorecard-final">
          <Trophy className="w-10 h-10 mx-auto" style={{ color: "#eab308" }} />
          <div className="text-xs uppercase tracking-wider mt-3" style={{ color: "var(--text-2)" }}>Final · {sc.team_a_score}–{sc.team_b_score}</div>
          <div className="font-display font-black text-3xl mt-1" data-testid="final-winner-name">{winnerName}</div>
          <div className="text-sm mt-1" style={{ color: "var(--text-2)" }}>wins the match</div>
          <div className="flex items-center justify-center gap-1.5 text-[10px] mono uppercase tracking-wider mt-4" style={{ color: "var(--text-2)" }}>
            <ShieldCheck className="w-3.5 h-3.5 text-primary-c" /> Scores locked · audit log synced to cloud
          </div>
        </div>
      ) : (
        <>
          <div className="rounded-3xl glow-border p-5 mb-4 text-center" style={{ background: "var(--surface)" }} data-testid="score-call-panel">
            <div className="flex items-center justify-center gap-2">
              <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-2)" }}>Score call</div>
              <MatchClock startedAt={sc.started_at} endedAt={sc.ended_at} className="text-[10px] text-primary-c" iconClass="w-3 h-3" />
            </div>
            <div className="font-display font-black tracking-tighter leading-none mt-1" style={{ fontSize: "3rem" }} data-testid="score-call">{sc.score_call}</div>
            <div className="text-xs mt-2" style={{ color: "var(--text-2)" }} data-testid="serve-status">
              Serving: <span className="text-primary-c font-bold">{servingName}</span>
              {sc.discipline === "doubles" ? ` · server ${sc.server_number}` : ""} · {sc.server_position} court
            </div>
            <div className="text-[10px] mono uppercase tracking-wider mt-1" style={{ color: "var(--text-2)" }}>game to {sc.target} · win by {sc.win_by}{sc.cap ? ` · cap ${sc.cap}` : ""}</div>
          </div>

          {/* PIP lock */}
          <button data-testid="pip-toggle" onClick={() => setPip((v) => !v)}
            className="w-full rounded-2xl py-3 mb-3 font-black tracking-wider flex items-center justify-center gap-2 transition-colors"
            style={pip
              ? { background: "rgba(239,68,68,0.16)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.5)" }
              : { background: "rgba(0,229,255,0.1)", color: "var(--primary-ink)", border: "1px solid rgba(0,229,255,0.4)" }}>
            {pip ? <><Lock className="w-4 h-4" /> POINT IN PROGRESS — LOCKED</> : <><Unlock className="w-4 h-4" /> PIP · tap to lock during rally</>}
          </button>

          {(() => {
            const zoneEls = {
              a: <KitchenZone key="a" side="a" name={data.a_name} score={sc.team_a_score} serving={sc.serving_team === "a"} position={sc.server_position}
                isWinner={sc.winner === "a"} locked={pip} faults={sc.fault_count} faultsTotal={sc.faults_to_sideout}
                band={sc.setup?.serving_bands?.a || (sc.setup?.serving_bands_team === "a" ? sc.setup?.serving_band_player : null)} onScore={() => rally("POINT")} />,
              b: <KitchenZone key="b" side="b" name={data.b_name} score={sc.team_b_score} serving={sc.serving_team === "b"} position={sc.server_position}
                isWinner={sc.winner === "b"} locked={pip} faults={sc.fault_count} faultsTotal={sc.faults_to_sideout}
                band={sc.setup?.serving_bands?.b || (sc.setup?.serving_bands_team === "b" ? sc.setup?.serving_band_player : null)} onScore={() => rally("POINT")} />,
            };
            const order = swapped ? ["b", "a"] : ["a", "b"];
            return (
              <>
                <div className="flex items-center justify-center gap-2 mb-2 text-[10px] mono uppercase tracking-wider" style={{ color: "var(--text-2)" }} data-testid="court-ends">
                  <span>{swapped ? "Right end" : "Left end"}</span>
                  <button data-testid="swap-sides" onClick={() => setSwapped((s) => !s)} className="flex items-center gap-1 glass rounded-full px-2.5 py-1" style={{ color: "var(--primary-ink)" }}>
                    <Repeat className="w-3 h-3" /> swap ends
                  </button>
                  <span>{swapped ? "Left end" : "Right end"}</span>
                </div>
                <div className="flex gap-3" style={{ opacity: pip ? 0.5 : 1 }} data-testid={`zones-order-${order.join("")}`}>
                  {order.map((s) => zoneEls[s])}
                </div>
              </>
            );
          })()}

          {sc.explanation && <p className="text-center text-xs mt-3" style={{ color: "var(--text-2)" }} data-testid="rally-explanation">{sc.explanation}</p>}

          <button data-testid="rally-fault" onClick={() => rally("FAULT")} disabled={busy || pip || sc.match_over}
            className="w-full rounded-2xl py-4 mt-3 font-black tracking-wider flex items-center justify-center gap-2 disabled:opacity-40"
            style={{ background: "rgba(239,68,68,0.16)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.4)" }}>
            <XCircle className="w-5 h-5" /> FAULT
            <span className="text-[10px] font-medium normal-case tracking-normal opacity-80">(auto side-out on 2nd)</span>
          </button>

          <div className="flex items-center justify-center gap-4 mt-4">
            <button data-testid="rally-undo" onClick={undo} disabled={busy || !sc.can_undo}
              className="glass rounded-full px-4 py-2 text-xs font-bold flex items-center gap-1.5 disabled:opacity-40" style={{ color: "var(--text)" }}>
              <Undo2 className="w-3.5 h-3.5" /> Undo
            </button>
            <span className="text-[10px] mono uppercase tracking-wider flex items-center gap-1.5" style={{ color: "var(--text-2)" }}><ShieldCheck className="w-3 h-3 text-primary-c" /> every action logged</span>
          </div>

          <button data-testid="finalize-match" onClick={finalize} disabled={busy || !sc.match_over}
            className="btn-glow w-full rounded-2xl py-4 mt-4 font-bold flex items-center justify-center gap-2 disabled:opacity-40 tracking-wider">
            <Flag className="w-4 h-4" /> {sc.match_over ? "FINALIZE MATCH" : "Reach game point to finalize"}
          </button>
        </>
      )}

      <AnimatePresence>
        {sideAlert && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: "rgba(0,0,0,0.7)" }}
            data-testid="side-change-alert" onClick={() => setSideAlert(false)}>
            <motion.div initial={{ scale: 0.85 }} animate={{ scale: 1 }} exit={{ scale: 0.85 }}
              className="rounded-3xl glow-border p-8 text-center max-w-sm" style={{ background: "var(--surface)" }}>
              <Repeat className="w-12 h-12 mx-auto text-primary-c" />
              <div className="font-display font-black text-3xl mt-4">SIDE CHANGE</div>
              <p className="text-sm mt-2" style={{ color: "var(--text-2)" }}>Halfway point reached — players switch ends of the court.</p>
              <button data-testid="side-change-dismiss" onClick={() => setSideAlert(false)} className="btn-glow rounded-full px-6 py-2.5 mt-6 font-bold">Got it</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
